import React, { useRef, useCallback } from 'react';
import { formatAmountDisplay } from '../utils/numberUtils';
import { resolveBankCodeInput } from '../utils/bankCodeAliases';
import { withdrawalAccountMemoFrom } from '../utils/remittanceWithdrawalMemo';

export type RemittanceDraftRow = {
  sourceId: string;
  /** 정산일보 행 날짜(YYYY-MM-DD) — API 업데이트용 */
  date?: string;
  /** 회사 보관용 엑셀(송금내역) B열 — 정산일보 차량번호 */
  vehicleNumber: string;
  /** 원본 운송구간(송금내역 엑셀 등) */
  route: string;
  colA: string;
  colB: string;
  /** 상호명 — 차량별 DB(driver_bank_accounts.business_name)에 저장 */
  businessName: string;
  /** 정산일보 지불운송비 (산재·송금액 계산 기준) */
  paymentAmount: number;
  colC: number;
  /** 산재보험 = 지불 × 49.9% × 1.76% ÷ 2 (10원 단위 반올림) */
  colSanje: number;
  colD: string;
  /** 정산일보 사업자번호 */
  colE: string;
  colF: string;
};

type Props = {
  rows: RemittanceDraftRow[];
  onRowsChange: React.Dispatch<React.SetStateAction<RemittanceDraftRow[]>>;
  /** A·B가 모두 있을 때 차량번호 기준 DB 저장(upsert) — 상호명 포함 */
  onPersistDriverBankAccount?: (row: RemittanceDraftRow) => void | Promise<void>;
  /** 예금주(이름) 편집 후 정산일보 driverName 반영 */
  onPersistDriverName?: (row: RemittanceDraftRow, newName: string) => void | Promise<void>;
};

/** 열별 최소 너비 — D(예금주)는 이름 표시 필수, E/F는 헤더 2줄 허용으로 폭 절약 */
function thMinWidthForLetter(letter: string): number {
  switch (letter) {
    case 'A':
      return 100;
    case 'B':
      return 220;
    case 'BN':
      return 160;
    case 'C':
      return 124;
    case 'SJ':
      return 100;
    case 'D':
      return 128;
    case 'E':
      return 100;
    case 'F':
      return 100;
    case 'K':
      return 120;
    case 'L':
      return 300;
    default:
      return 88;
  }
}

function persistIfComplete(
  row: RemittanceDraftRow,
  onPersist: Props['onPersistDriverBankAccount']
) {
  if (!onPersist) return;
  const vn = String(row.vehicleNumber || '').trim();
  const resolvedA = resolveBankCodeInput(row.colA);
  const merged = { ...row, colA: resolvedA };
  if (!vn || !String(resolvedA).trim() || !String(merged.colB || '').trim()) return;
  void onPersist(merged);
}

/** 송금·대량이체 표 (모달: 은행코드~운송구간, 엑셀은 A~H) */
export function RemittanceEditTable({
  rows,
  onRowsChange,
  onPersistDriverBankAccount,
  onPersistDriverName,
}: Props) {
  const refsA = useRef<(HTMLInputElement | null)[]>([]);
  const refsB = useRef<(HTMLInputElement | null)[]>([]);
  const refsBN = useRef<(HTMLInputElement | null)[]>([]);

  /** 은행코드 → 계좌번호 → 상호명 → 다음 행 은행코드 … 가로 순서 */
  const focusRemittanceCell = useCallback((col: 'A' | 'B' | 'BN', rowIndex: number) => {
    const arr = col === 'A' ? refsA.current : col === 'B' ? refsB.current : refsBN.current;
    const el = arr[rowIndex];
    if (el) {
      el.focus();
      try {
        el.select();
      } catch {
        /* ignore */
      }
    }
  }, []);

  const onRemittanceEditableKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, col: 'A' | 'B' | 'BN', idx: number) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (idx < rows.length - 1) {
          focusRemittanceCell(col, idx + 1);
        }
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (idx > 0) {
          focusRemittanceCell(col, idx - 1);
        }
        return;
      }

      const t = e.currentTarget;
      const s = t.selectionStart ?? 0;
      const n = t.selectionEnd ?? 0;
      const len = t.value.length;
      const collapsed = s === n;

      if (e.key === 'ArrowLeft' && collapsed) {
        if (col === 'BN' && s === 0) {
          e.preventDefault();
          focusRemittanceCell('B', idx);
          return;
        }
        if (col === 'B' && s === 0) {
          e.preventDefault();
          focusRemittanceCell('A', idx);
          return;
        }
        if (col === 'A' && s === 0 && idx > 0) {
          e.preventDefault();
          focusRemittanceCell('BN', idx - 1);
          return;
        }
      }

      if (e.key === 'ArrowRight' && collapsed) {
        if (col === 'A' && s === len) {
          e.preventDefault();
          focusRemittanceCell('B', idx);
          return;
        }
        if (col === 'B' && s === len) {
          e.preventDefault();
          focusRemittanceCell('BN', idx);
          return;
        }
        if (col === 'BN' && s === len && idx < rows.length - 1) {
          e.preventDefault();
          focusRemittanceCell('A', idx + 1);
          return;
        }
      }
    },
    [rows.length, focusRemittanceCell]
  );

  return (
    <>
      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, width: '100%' }}>
        <table
          style={{
            borderCollapse: 'collapse',
            width: '100%',
            minWidth: 1480,
            fontSize: 13,
            tableLayout: 'auto'
          }}
        >
          <thead>
            <tr style={{ background: '#f1f5f9', fontSize: 12, color: '#334155' }}>
              <th
                style={{
                  padding: '8px 6px',
                  border: '1px solid #e5e7eb',
                  width: 40,
                  position: 'sticky',
                  left: 0,
                  background: '#f1f5f9',
                  zIndex: 1,
                  fontWeight: 700
                }}
              >
                #
              </th>
              <th
                style={{
                  padding: '8px 6px',
                  border: '1px solid #e5e7eb',
                  minWidth: thMinWidthForLetter('A'),
                  whiteSpace: 'nowrap',
                  fontWeight: 700
                }}
              >
                은행코드
              </th>
              <th
                style={{
                  padding: '8px 6px',
                  border: '1px solid #e5e7eb',
                  minWidth: thMinWidthForLetter('B'),
                  whiteSpace: 'nowrap',
                  fontWeight: 700
                }}
              >
                계좌번호
              </th>
              <th
                style={{
                  padding: '8px 6px',
                  border: '1px solid #e5e7eb',
                  minWidth: thMinWidthForLetter('BN'),
                  whiteSpace: 'nowrap',
                  fontWeight: 700
                }}
              >
                상호명
              </th>
              <th
                style={{
                  padding: '8px 6px',
                  border: '1px solid #e5e7eb',
                  minWidth: thMinWidthForLetter('C'),
                  whiteSpace: 'nowrap',
                  fontWeight: 700
                }}
              >
                송금액(부가세포함)
              </th>
              <th
                style={{
                  padding: '8px 6px',
                  border: '1px solid #e5e7eb',
                  minWidth: thMinWidthForLetter('SJ'),
                  whiteSpace: 'nowrap',
                  fontWeight: 700,
                  boxSizing: 'border-box'
                }}
              >
                산재보험
              </th>
              <th
                style={{
                  padding: '8px 6px',
                  border: '1px solid #e5e7eb',
                  minWidth: thMinWidthForLetter('D'),
                  whiteSpace: 'nowrap',
                  fontWeight: 700,
                  boxSizing: 'border-box'
                }}
              >
                예금주
              </th>
              <th
                style={{
                  padding: '8px 6px',
                  border: '1px solid #e5e7eb',
                  minWidth: thMinWidthForLetter('E'),
                  maxWidth: 112,
                  width: 104,
                  whiteSpace: 'normal',
                  lineHeight: 1.25,
                  fontWeight: 700,
                  boxSizing: 'border-box',
                  verticalAlign: 'middle'
                }}
              >
                사업자번호
              </th>
              <th
                style={{
                  padding: '8px 6px',
                  border: '1px solid #e5e7eb',
                  minWidth: thMinWidthForLetter('F'),
                  maxWidth: 112,
                  width: 104,
                  whiteSpace: 'normal',
                  lineHeight: 1.25,
                  fontWeight: 700,
                  boxSizing: 'border-box',
                  verticalAlign: 'middle'
                }}
              >
                출금계좌메모
              </th>
              <th
                style={{
                  padding: '8px 6px',
                  border: '1px solid #e5e7eb',
                  minWidth: thMinWidthForLetter('K'),
                  whiteSpace: 'nowrap',
                  fontWeight: 700
                }}
              >
                차량번호
              </th>
              <th
                style={{
                  padding: '8px 6px',
                  border: '1px solid #e5e7eb',
                  minWidth: thMinWidthForLetter('L'),
                  whiteSpace: 'nowrap',
                  fontWeight: 700
                }}
              >
                운송구간
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={row.sourceId} style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                <td style={{ padding: 4, border: '1px solid #e5e7eb', textAlign: 'center', position: 'sticky', left: 0, background: idx % 2 === 0 ? '#fff' : '#fafafa', zIndex: 1 }}>
                  {idx + 1}
                </td>
                <td
                  style={{
                    padding: 2,
                    border: '1px solid #e5e7eb',
                    overflow: 'hidden',
                    minWidth: 92,
                    verticalAlign: 'middle',
                    boxSizing: 'border-box'
                  }}
                >
                  <input
                    ref={el => {
                      refsA.current[idx] = el;
                    }}
                    value={row.colA}
                    onKeyDown={e => onRemittanceEditableKeyDown(e, 'A', idx)}
                    onChange={e => {
                      const v = e.target.value;
                      onRowsChange(prev => prev.map((r, i) => (i === idx ? { ...r, colA: v } : r)));
                    }}
                    onBlur={e => {
                      const resolved = resolveBankCodeInput(e.target.value);
                      const merged = { ...row, colA: resolved };
                      if (resolved !== row.colA) {
                        onRowsChange(prev =>
                          prev.map((r, i) => (i === idx ? { ...r, colA: resolved } : r))
                        );
                      }
                      persistIfComplete(merged, onPersistDriverBankAccount);
                    }}
                    title="은행·증권명 또는 약어 입력 후 다른 칸으로 이동하면 3자리 코드로 바뀝니다."
                    style={{
                      width: '100%',
                      minWidth: 0,
                      maxWidth: '100%',
                      padding: '6px 8px',
                      border: '1px solid #cbd5e1',
                      borderRadius: 4,
                      boxSizing: 'border-box',
                      display: 'block'
                    }}
                    placeholder="은행코드"
                  />
                </td>
                <td style={{ padding: 2, border: '1px solid #e5e7eb', minWidth: 220 }}>
                  <input
                    ref={el => {
                      refsB.current[idx] = el;
                    }}
                    value={row.colB}
                    onKeyDown={e => onRemittanceEditableKeyDown(e, 'B', idx)}
                    onChange={e => {
                      const v = e.target.value;
                      onRowsChange(prev => prev.map((r, i) => (i === idx ? { ...r, colB: v } : r)));
                    }}
                    onBlur={() => {
                      const resolvedA = resolveBankCodeInput(row.colA);
                      const merged = { ...row, colA: resolvedA, colB: row.colB };
                      if (resolvedA !== row.colA) {
                        onRowsChange(prev =>
                          prev.map((r, i) => (i === idx ? { ...r, colA: resolvedA } : r))
                        );
                      }
                      persistIfComplete(merged, onPersistDriverBankAccount);
                    }}
                    style={{
                      width: '100%',
                      minWidth: 200,
                      padding: '6px 8px',
                      border: '1px solid #cbd5e1',
                      borderRadius: 4,
                      boxSizing: 'border-box',
                      fontFamily: 'ui-monospace, Consolas, monospace'
                    }}
                    placeholder="계좌번호"
                  />
                </td>
                <td style={{ padding: 2, border: '1px solid #e5e7eb', minWidth: 140 }}>
                  <input
                    ref={el => {
                      refsBN.current[idx] = el;
                    }}
                    value={row.businessName}
                    onKeyDown={e => onRemittanceEditableKeyDown(e, 'BN', idx)}
                    onChange={e => {
                      const v = e.target.value;
                      onRowsChange(prev =>
                        prev.map((r, i) =>
                          i === idx
                            ? {
                                ...r,
                                businessName: v,
                                colF: withdrawalAccountMemoFrom(v, r.vehicleNumber),
                              }
                            : r
                        )
                      );
                    }}
                    onBlur={e => {
                      const v = e.target.value;
                      persistIfComplete(
                        { ...row, businessName: v, colF: withdrawalAccountMemoFrom(v, row.vehicleNumber) },
                        onPersistDriverBankAccount
                      );
                    }}
                    placeholder="상호명"
                    style={{
                      width: '100%',
                      minWidth: 120,
                      padding: '6px 8px',
                      border: '1px solid #cbd5e1',
                      borderRadius: 4,
                      boxSizing: 'border-box'
                    }}
                  />
                </td>
                <td
                  style={{
                    padding: 2,
                    border: '1px solid #e5e7eb',
                    overflow: 'hidden',
                    minWidth: 118,
                    verticalAlign: 'middle',
                    boxSizing: 'border-box'
                  }}
                >
                  <input
                    readOnly
                    value={formatAmountDisplay(row.colC)}
                    title={formatAmountDisplay(row.colC)}
                    style={{
                      width: '100%',
                      minWidth: 0,
                      maxWidth: '100%',
                      padding: '6px 8px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 4,
                      boxSizing: 'border-box',
                      background: '#f1f5f9',
                      color: '#0f172a',
                      display: 'block',
                      textAlign: 'right',
                      fontFamily: 'ui-monospace, Consolas, "Courier New", monospace',
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  />
                </td>
                <td
                  style={{
                    padding: 2,
                    border: '1px solid #e5e7eb',
                    overflow: 'hidden',
                    minWidth: thMinWidthForLetter('SJ'),
                    verticalAlign: 'middle',
                    boxSizing: 'border-box'
                  }}
                >
                  <input
                    readOnly
                    value={formatAmountDisplay(row.colSanje)}
                    title={formatAmountDisplay(row.colSanje)}
                    style={{
                      width: '100%',
                      minWidth: 0,
                      maxWidth: '100%',
                      padding: '6px 8px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 4,
                      boxSizing: 'border-box',
                      background: '#f8fafc',
                      color: '#0f172a',
                      display: 'block',
                      textAlign: 'right',
                      fontFamily: 'ui-monospace, Consolas, "Courier New", monospace',
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  />
                </td>
                <td
                  style={{
                    padding: 2,
                    border: '1px solid #e5e7eb',
                    overflow: 'hidden',
                    minWidth: thMinWidthForLetter('D'),
                    verticalAlign: 'middle',
                    boxSizing: 'border-box'
                  }}
                >
                  <input
                    value={row.colD}
                    title={row.colD}
                    onChange={e => {
                      const v = e.target.value;
                      onRowsChange(prev =>
                        prev.map((r, i) => (i === idx ? { ...r, colD: v } : r))
                      );
                    }}
                    onBlur={e => {
                      if (onPersistDriverName) {
                        void onPersistDriverName(row, e.target.value.trim());
                      }
                    }}
                    placeholder="예금주"
                    style={{
                      width: '100%',
                      minWidth: 0,
                      maxWidth: '100%',
                      padding: '6px 8px',
                      border: '1px solid #cbd5e1',
                      borderRadius: 4,
                      background: '#fff',
                      color: '#0f172a',
                      boxSizing: 'border-box',
                      display: 'block'
                    }}
                  />
                </td>
                <td
                  style={{
                    padding: 2,
                    border: '1px solid #e5e7eb',
                    overflow: 'hidden',
                    minWidth: thMinWidthForLetter('E'),
                    maxWidth: 140,
                    verticalAlign: 'middle',
                    boxSizing: 'border-box'
                  }}
                >
                  <input
                    readOnly
                    value={row.colE}
                    title={row.colE}
                    style={{
                      width: '100%',
                      minWidth: 0,
                      maxWidth: '100%',
                      padding: '6px 8px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 4,
                      background: '#f1f5f9',
                      color: '#0f172a',
                      boxSizing: 'border-box',
                      display: 'block',
                      fontFamily: 'ui-monospace, Consolas, monospace',
                      fontSize: 12
                    }}
                  />
                </td>
                <td
                  style={{
                    padding: 2,
                    border: '1px solid #e5e7eb',
                    overflow: 'hidden',
                    minWidth: thMinWidthForLetter('F'),
                    maxWidth: 120,
                    verticalAlign: 'middle',
                    boxSizing: 'border-box'
                  }}
                >
                  <input
                    readOnly
                    value={row.colF}
                    title={row.colF}
                    style={{
                      width: '100%',
                      minWidth: 0,
                      maxWidth: '100%',
                      padding: '6px 8px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 4,
                      background: '#f1f5f9',
                      color: '#0f172a',
                      boxSizing: 'border-box',
                      display: 'block'
                    }}
                  />
                </td>
                <td
                  style={{
                    padding: 2,
                    border: '1px solid #e5e7eb',
                    overflow: 'hidden',
                    minWidth: 100,
                    verticalAlign: 'middle',
                    boxSizing: 'border-box'
                  }}
                >
                  <input
                    readOnly
                    value={row.vehicleNumber}
                    title={row.vehicleNumber}
                    style={{
                      width: '100%',
                      minWidth: 0,
                      maxWidth: '100%',
                      padding: '6px 8px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 4,
                      background: '#f1f5f9',
                      boxSizing: 'border-box',
                      display: 'block'
                    }}
                  />
                </td>
                <td
                  style={{
                    padding: 2,
                    border: '1px solid #e5e7eb',
                    overflow: 'hidden',
                    minWidth: 200,
                    verticalAlign: 'middle',
                    boxSizing: 'border-box'
                  }}
                >
                  <input
                    readOnly
                    value={row.route}
                    title={row.route}
                    style={{
                      width: '100%',
                      minWidth: 0,
                      maxWidth: '100%',
                      padding: '6px 8px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 4,
                      background: '#f1f5f9',
                      boxSizing: 'border-box',
                      display: 'block'
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
